import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useAuthStore } from './stores/authStore';
import { sessionManager } from './utils/sessionManager';
import { detectAppMode } from './utils/domainDetection';
import { CartProvider } from './contexts/CartContext';
import { LocationProvider } from './contexts/LocationContext';
import ToastContainer from './components/Toast';
import PageViewTracker from './components/PageViewTracker';
import ShopRoutes from './routes/ShopRoutes';
import PortalRoutes from './routes/PortalRoutes';
import AdminApp from './routes/AdminApp';

const PageSpinner = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <div
      style={{
        width: 32,
        height: 32,
        border: '3px solid #e5e7eb',
        borderTopColor: '#111827',
        borderRadius: '50%',
        animation: 'spin 0.6s linear infinite',
      }}
    />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        // Don't retry 429 (axios interceptor handles backoff) or 401/403
        const status = error?.response?.status;
        if (status === 429 || status === 401 || status === 403) return false;
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  },
});

/**
 * Route switcher that lives INSIDE the Router so it re-renders on URL changes.
 * detectAppMode() reads window.location.pathname — it must re-run when
 * React Router navigates (e.g. Login → /admin), not just on hard refresh.
 */
function AppRoutes({ isAuthenticated }: { isAuthenticated: boolean }) {
  useLocation(); // subscribe to URL changes so detectAppMode() re-evaluates
  const appMode = detectAppMode();

  return (
    <>
      <PageViewTracker />
      <div className="App">
        <Suspense fallback={<PageSpinner />}>
          {appMode === 'shop' && <ShopRoutes isAuthenticated={isAuthenticated} />}
          {appMode === 'portal' && <PortalRoutes isAuthenticated={isAuthenticated} />}
          {appMode === 'admin' && <AdminApp isAuthenticated={isAuthenticated} />}
        </Suspense>
      </div>
      <ToastContainer />
    </>
  );
}

function App() {
  const { isAuthenticated, isLoading, user, getCurrentUser } = useAuthStore();

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token && !user) {
      getCurrentUser();
    }
  }, [getCurrentUser, user, isLoading, isAuthenticated]);

  useEffect(() => {
    const appMode = detectAppMode();
    if (isAuthenticated && (appMode === 'portal' || appMode === 'admin')) {
      sessionManager.start();
    } else {
      sessionManager.stop();
    }
    return () => { sessionManager.stop(); };
  }, [isAuthenticated]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-900 border-t-transparent" />
          <span className="text-gray-600 font-medium">Loading ARKX...</span>
        </div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <CartProvider>
        <LocationProvider>
          <Router>
            <AppRoutes isAuthenticated={isAuthenticated} />
          </Router>
        </LocationProvider>
      </CartProvider>
      {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

export default App;
